import { ACCEPTED_VIDEO_TYPES, MAX_VIDEO_FILE_SIZE } from '@eigtech/meetings-types'
import { Corn } from '@eigtech/shared-corn'
import { FileEntry, GalleryUploadForm } from '@eigtech/ui-shared-files'
import { createForm } from '@eigtech/ui-shared-forms/shadcn'
import { useRef, useState } from 'react'
import { MultipartUploader } from '../../utils/MultipartUploader'
import { VideoFileUploadForm, VideoFileUploadSchema, maximumUploadsCount } from './fileUploadSchema'

export type UploadMeetingVideoProps = {
  entityId: Corn
}

export function UploadMeetingVideo({ entityId }: UploadMeetingVideoProps) {
  const form = useForm({ defaultValues: { files: [] } })

  // const [failedUploads, setFailedUploads] = useState<string[]>([])
  const [numberOfFilesUploaded, setNumberOfFilesUploaded] = useState(0)
  const [numberOfFiles, setNumberOfFiles] = useState(0)
  const [uploadError, setUploadError] = useState<string | null>(null)

  // Separate ref holds the actual File objects — cleared as soon as each upload starts
  const fileRefs = useRef<Map<string, File>>(new Map())

  async function onSubmit({ files }: VideoFileUploadForm) {
    setUploadError(null)

    setNumberOfFilesUploaded(0)
    setNumberOfFiles(files.length)

    const now = Date.now()

    const promises = files.map(({ file, ...meta }, index) => {
      return new Promise((resolve, reject) => {
        const extension = file.name.split('.').pop()
        const newFilename = meta.notes ? `${meta.notes}.${extension}` : file.name
        // startTime needs to be different between videos or the meeting IDs will conflict
        const startTime = new Date(now + index)
        const startTimeIso = startTime.toISOString()

        const fileToUpload = file.getFile()
        if (!fileToUpload) {
          return
        }

        fileRefs.current.set(
          file.id,
          new File([fileToUpload], newFilename, {
            lastModified: startTime.getTime(),
            type: file.type,
          })
        )

        const updatedFile: FileEntry = {
          ...file,
          name: newFilename,
          getFile: () => fileRefs.current.get(file.id) ?? null,
          revokeFile: () => {
            file.revokeFile()
            fileRefs.current.delete(file.id)
          },
        }

        const uploader = new MultipartUploader({
          file: updatedFile.getFile()!,
          meetingInfo: {
            description: meta.description ?? '',
            entityId,
            metadata: meta,
            notes: meta.notes ?? '',
            requiresStitching: false,
            startTime: startTimeIso,
          },
          threadsQuantity: 1,
        })

        uploader.onError((error) => {
          uploader.abort()
          updatedFile.updateFile({ ...updatedFile, status: 'error' })
          reject(error)
        })

        uploader.onComplete(() => {
          file.updateFile({ ...file, status: 'uploaded' })
          file.revokeFile()
          setNumberOfFilesUploaded((prev) => prev + 1)
          resolve(true)
        })

        uploader.start()
      })
    })

    const results = await Promise.allSettled(promises)

    const hasFailure = results.some(
      (result): result is PromiseRejectedResult => result.status === 'rejected'
    )
    if (hasFailure) {
      setUploadError('Failed to upload some files. Files that failed to upload have been marked.')
    }
  }

  return (
    <GalleryUploadForm<typeof VideoFileUploadSchema>
      ArrayField={ArrayField}
      GalleryItemFields={GalleryItemFields}
      accept={ACCEPTED_VIDEO_TYPES.join(',')}
      acceptedTypesLabel="Videos"
      form={form}
      formId="uploadMeetingVideoForm"
      isPendingUploadUrl={false}
      isUploadError={!!uploadError}
      isUploadUrlError={false}
      maxFiles={maximumUploadsCount}
      maxSize={MAX_VIDEO_FILE_SIZE}
      numberOfFiles={numberOfFiles}
      numberOfFilesUploaded={numberOfFilesUploaded}
      uploadError={uploadError}
      onSubmit={onSubmit}
    />
  )
}

function ArrayField({ children }: { children: React.ReactNode }) {
  return (
    <BaseArrayField emptyMessage="" label="" name="files">
      {children}
    </BaseArrayField>
  )
}

function GalleryItemFields() {
  const { name } = useArrayFieldRowContext()

  return (
    <>
      <InputField label="Filename" name={`${name}.notes`} />
      <TextareaField label="File Description" name={`${name}.description`} />
    </>
  )
}

const {
  useForm,
  ArrayField: BaseArrayField,
  InputField,
  TextareaField,
  useArrayFieldRowContext,
} = createForm(VideoFileUploadSchema, 'meetingVideoUploadForm')
